import { useCallback } from 'react';
import { useMachineContext } from '../../../pages/machine/machine-context-provider';
import { useRegisterAndSubscribeToMachineBindingSubscription } from '../../graphql/generated';
import { useRobotOperations } from './robot-operations.binding';
import {
  SynchronousBindingOptions,
  useSynchronousBindingLazy,
} from './synchronous.binding';

export interface ScriptActionBindingContext {
  actionName?: string;
  script?: string;
  validProgramStates?: string[];
  onStop?: Omit<ScriptActionBindingContext, 'onStop'>;
}

export interface ScriptActionBindingResult {
  success: boolean;
}

export const useScriptActionBinding = (
  machineId: string,
  context: ScriptActionBindingContext,
  options?: { skip?: boolean }
): ScriptActionBindingResult | undefined => {
  const { data } = useRegisterAndSubscribeToMachineBindingSubscription({
    skip: options?.skip,
    variables: {
      input: {
        machineId,
        binding: {
          namespace: 'machine',
          type: 'scriptAction',
          context,
        },
      },
    },
  });

  return data?.registerAndSubscribeToMachineBindingResult?.data;
};

export const useScriptActionBindingLazy = (
  options?: Pick<
    SynchronousBindingOptions<ScriptActionBindingResult>,
    'closeImmediately'
  >
) => {
  const { send, close } = useSynchronousBindingLazy<
    ScriptActionBindingContext,
    ScriptActionBindingResult
  >({
    namespace: 'machine',
    type: 'scriptAction',
    closeImmediately: options?.closeImmediately,
  });

  return { send, close };
};

export const useScriptActionExecuteLazy = (
  machineId: string,
  context: ScriptActionBindingContext
): (() => Promise<boolean>) => {
  const { send: executeScript } = useScriptActionBindingLazy();

  const cb = useCallback(async (): Promise<boolean> => {
    const result = await executeScript(machineId, context);
    return result?.success ?? false;
  }, [context, executeScript, machineId]);

  return cb;
};

export const useStopProgram = (): (() => Promise<boolean>) => {
  const { machine } = useMachineContext();

  const { stopProgram } = useRobotOperations();

  return async () => {
    const result = await stopProgram(machine.id);
    return result.success;
  };
};
